// Money math (SPEC §2.3): integer minor units as BigInt, rates as six-decimal strings. Nothing in
// this file multiplies, divides or rounds a JS float. Amounts cross the API in major units (a
// decimal string, or a number read through String(n)); results are decimal strings fixed to the
// currency's decimals.
import { MINOR_UNIT_EXCEPTIONS, OFFERED_CURRENCIES, SYMBOLS } from './currencies.js';

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const TEN = 10n;
const RATE_SCALE = 1_000_000n; // a rate is carried as micro-units: "0.758301" → 758301n
const USD_DECIMALS = 2;

/** True for the offered list plus the minor-unit table's keys (SPEC §2.2 check 1, §2.10). */
export function isKnownCurrency(code) {
  if (typeof code !== 'string') return false;
  return OFFERED_CURRENCIES.includes(code) || hasOwn(MINOR_UNIT_EXCEPTIONS, code);
}

/** Decimals of a currency: the exception table, else 2. */
export function minorUnits(code) {
  return typeof code === 'string' && hasOwn(MINOR_UNIT_EXCEPTIONS, code)
    ? MINOR_UNIT_EXCEPTIONS[code]
    : 2;
}

function pow10(exponent) {
  return TEN ** BigInt(exponent);
}

/**
 * Integer division of a non-negative BigInt, rounded half-up, up (ceiling) or down (floor).
 * Exported for ladder.js; not part of the public API.
 */
export function divRound(numerator, denominator, rounding = 'half-up') {
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  if (remainder === 0n) return quotient;
  if (rounding === 'down') return quotient;
  if (rounding === 'up') return quotient + 1n;
  if (rounding === 'half-up') return remainder * 2n >= denominator ? quotient + 1n : quotient;
  throw new RangeError(`unknown rounding: ${String(rounding)}`);
}

// Signed division: rounds the magnitude, then restores the sign.
function divRoundSigned(numerator, denominator, rounding) {
  if (numerator < 0n) return -divRound(-numerator, denominator, rounding);
  return divRound(numerator, denominator, rounding);
}

const MAJOR_PATTERN = /^-?(?:\d+\.?\d*|\.\d+)$/;

/**
 * The §2.3 parser. Numbers go through String(n); accepts "40", "40." and ".5" with an optional
 * leading "-"; refuses "", whitespace, exponent form, NaN and Infinity with RangeError.
 * Returns the value as { digits, scale }: value = digits / 10^scale, digits a signed BigInt.
 * Exported for donorAmount.js (half-even USD cents); not part of the public API.
 */
export function parseMajor(amount) {
  let text;
  if (typeof amount === 'number') text = String(amount);
  else if (typeof amount === 'string') text = amount;
  else throw new RangeError('amount must be a decimal string or a number');
  if (!MAJOR_PATTERN.test(text)) throw new RangeError(`not a decimal amount: "${text}"`);
  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const dot = body.indexOf('.');
  const whole = dot === -1 ? body : body.slice(0, dot);
  const fraction = dot === -1 ? '' : body.slice(dot + 1);
  const digits = BigInt(`${whole || '0'}${fraction}`);
  return { digits: negative ? -digits : digits, scale: fraction.length };
}

/** Major units → BigInt minor units of `currency`, rounded on magnitude (default half-up). */
export function toMinorUnits(amount, currency, opts = {}) {
  const { digits, scale } = parseMajor(amount);
  const decimals = minorUnits(currency);
  if (scale <= decimals) return digits * pow10(decimals - scale);
  return divRoundSigned(digits, pow10(scale - decimals), opts.rounding ?? 'half-up');
}

/** BigInt minor units → a decimal string fixed to the currency's decimals. */
export function fromMinorUnits(minor, currency) {
  if (typeof minor !== 'bigint') throw new TypeError('minor units must be a BigInt');
  const decimals = minorUnits(currency);
  const negative = minor < 0n;
  const magnitude = (negative ? -minor : minor).toString();
  const sign = negative ? '-' : '';
  if (decimals === 0) return `${sign}${magnitude}`;
  const padded = magnitude.padStart(decimals + 1, '0');
  return `${sign}${padded.slice(0, -decimals)}.${padded.slice(-decimals)}`;
}

const RATE_PATTERN = /^\d+\.\d{6}$/;

/** A six-decimal rate string → BigInt micro-units; refuses any other form, and zero. */
export function parseRate(fxRate) {
  if (typeof fxRate !== 'string' || !RATE_PATTERN.test(fxRate)) {
    throw new RangeError('fxRate must be a string with exactly six decimals');
  }
  const micro = BigInt(fxRate.replace('.', ''));
  if (micro === 0n) throw new RangeError('fxRate must be greater than zero');
  return micro;
}

// USD cents → display minor units: round(usdCents × rateMicro × 10^dispDec, 10^2 × 10^6).
export function usdCentsToDisplayMinor(usdCents, rateMicro, currency, rounding = 'half-up') {
  const numerator = usdCents * rateMicro * pow10(minorUnits(currency));
  return divRoundSigned(numerator, pow10(USD_DECIMALS) * RATE_SCALE, rounding);
}

// Display minor units → USD cents: round(dispMinor × 10^2 × 10^6, rateMicro × 10^dispDec).
export function displayMinorToUsdCents(displayMinor, rateMicro, currency, rounding = 'half-up') {
  const numerator = displayMinor * pow10(USD_DECIMALS) * RATE_SCALE;
  return divRoundSigned(numerator, rateMicro * pow10(minorUnits(currency)), rounding);
}

/**
 * USD → display currency. The USD input is first read to cents (half-up); `rounding` applies to
 * the conversion into the display currency's minor units.
 */
export function toDisplay(usd, fxRate, currency, opts = {}) {
  const rateMicro = parseRate(fxRate);
  const usdCents = toMinorUnits(usd, 'USD');
  return fromMinorUnits(
    usdCentsToDisplayMinor(usdCents, rateMicro, currency, opts.rounding),
    currency,
  );
}

/**
 * Display currency → USD, 2 dp. The display input is first read to the currency's minor units
 * (half-up); `rounding` applies to the conversion into cents.
 */
export function toUsd(display, fxRate, currency, opts = {}) {
  const rateMicro = parseRate(fxRate);
  const displayMinor = toMinorUnits(display, currency);
  return fromMinorUnits(
    displayMinorToUsdCents(displayMinor, rateMicro, currency, opts.rounding),
    'USD',
  );
}

/**
 * The decimal separator of a locale, from Intl's number parts. An absent or unreadable locale
 * gives "." (today's behaviour).
 */
export function decimalSeparatorFor(locale) {
  if (typeof locale !== 'string' || locale === '') return '.';
  try {
    const decimal = new Intl.NumberFormat(locale)
      .formatToParts(1.1)
      .find((p) => p.type === 'decimal');
    return decimal && decimal.value === ',' ? ',' : '.';
  } catch {
    return '.';
  }
}

const WHITESPACE = /\s/g; // \s covers the no-break and narrow no-break spaces (Unicode Zs)
const LOOSE_SYMBOLS = ['≈', '$', '£', '€'];

// Removes whitespace, the currency's ISO code, every SYMBOLS entry (longest first) and the loose
// symbol characters a donor might type or paste.
function stripDecorations(text, currency) {
  let out = text.replace(WHITESPACE, '');
  if (typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency)) {
    out = out.replace(new RegExp(currency, 'gi'), '');
  }
  const symbols = Object.values(SYMBOLS).sort((a, b) => b.length - a.length);
  for (const symbol of [...symbols, ...LOOSE_SYMBOLS]) out = out.split(symbol).join('');
  return out;
}

function canonical(whole, fraction) {
  const trimmed = whole.replace(/^0+(?=\d)/, '');
  const head = trimmed === '' ? '0' : trimmed;
  return fraction === '' ? head : `${head}.${fraction}`;
}

const failure = (reason) => ({ ok: false, reason });

function finish(whole, fraction, decimals) {
  if (!/^\d*$/.test(whole) || !/^\d*$/.test(fraction)) return failure('invalid');
  if (whole === '' && fraction === '') return failure('invalid');
  if (fraction.length > decimals) return failure('too-many-decimals');
  return { ok: true, amount: canonical(whole, fraction) };
}

// A dot-decimal locale: commas group, one dot is the decimal (today's parsing).
function parseDotLocale(body, decimals) {
  const ungrouped = body.split(',').join('');
  const parts = ungrouped.split('.');
  if (parts.length > 2) return failure('invalid');
  return finish(parts[0], parts[1] ?? '', decimals);
}

// A decimal-comma locale (D-R29, SPEC §2.3 "Custom amount").
function parseCommaLocale(body, decimals) {
  const commas = body.split(',').length - 1;
  const decimalComma =
    decimals > 0 && commas === 1 && new RegExp(`,\\d{1,${decimals}}$`).test(body);
  if (decimalComma) {
    const [head, fraction] = body.split(',');
    // With a decimal comma present, every dot is grouping ("1.000,50"): 3 digits after each.
    const groups = head.split('.');
    if (!groups.slice(1).every((group) => /^\d{3}$/.test(group))) return failure('invalid');
    return finish(groups.join(''), fraction, decimals);
  }
  const ungrouped = body.split(',').join('');
  const groups = ungrouped.split('.');
  if (groups.length === 1) return finish(ungrouped, '', decimals);
  if (groups.length > 2) {
    // "1.000.000": only grouping is possible, so every group after a dot must be 3 digits.
    if (!groups.slice(1).every((group) => /^\d{3}$/.test(group))) return failure('invalid');
    return finish(groups.join(''), '', decimals);
  }
  const [head, tail] = groups;
  if (/^\d{3}$/.test(tail)) {
    // "1.000": grouping in this locale, but a decimal to a dot-locale reader. A 0-decimal currency
    // has no decimals, so it can only be grouping; in a 2- or 3-decimal currency the form asks.
    if (decimals === 0) return finish(`${head}${tail}`, '', decimals);
    return failure('ambiguous-dot');
  }
  // "40.5", "40.", ".5": a dot with any other digit count reads as the decimal.
  return finish(head, tail, decimals);
}

/**
 * Reads a typed custom amount in the donor's locale (D-R29). Returns a canonical major-unit
 * string ("40.50", "1000"), never a float, or the reason it was refused. Never rounds.
 */
export function parseAmountInput(text, currency, opts = {}) {
  if (text === null || text === undefined) return failure('empty');
  if (typeof text !== 'string') return failure('invalid');
  const body = stripDecorations(text, currency);
  if (body === '') return failure('empty');
  if (!/^[\d.,]+$/.test(body)) return failure('invalid');
  const decimals = minorUnits(currency);
  if (decimalSeparatorFor(opts.locale) === ',') return parseCommaLocale(body, decimals);
  return parseDotLocale(body, decimals);
}
